/**
 * Projects map view — clustered pins + a search/list panel.
 *
 *   mode   'portal' (default) | 'admin'      → link target and name formatting
 *   layout 'embedded' (default) | 'fullscreen' → bordered box with side list,
 *          or edge-to-edge map with a floating panel and overlay controls
 *
 * Coordinates come from the project row. With a Mapbox token, projects that
 * still lack coordinates are geocoded client-side as a fallback; without one,
 * a free OpenStreetMap basemap is used and only stored coordinates are shown.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import type { GeoJSONSource, MapLayerMouseEvent } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './ProjectsMapView.css';
import type { Project } from '../../../shared/types/project';
import { formatProjectName } from '../../../shared/formatProjectName';

interface Props {
  projects: Project[];
  mode?: 'portal' | 'admin';
  layout?: 'embedded' | 'fullscreen';
  /** Admin only: called when the user clicks "Locate projects". Caller should reload projects afterwards. */
  onLocate?: () => Promise<void>;
  /** Embedded layout only: height of the box. */
  height?: string | number;
  /** Controlled search: when provided, the panel's search box edits the parent's value and the parent does the filtering. */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Fullscreen: controls floated at the top-left of the map (view toggle, actions). */
  overlayTopLeft?: React.ReactNode;
  /** Extra controls rendered in the panel under the search box (filters). */
  panelExtras?: React.ReactNode;
}

type Basemap = 'dark' | 'satellite' | 'hybrid' | 'osm';
const MAPBOX_STYLES: Record<Exclude<Basemap, 'osm'>, { label: string; mapboxStyle: string }> = {
  dark:      { label: 'Standard',  mapboxStyle: 'dark-v11' },
  satellite: { label: 'Satellite', mapboxStyle: 'satellite-v9' },
  hybrid:    { label: 'Hybrid',    mapboxStyle: 'satellite-streets-v12' },
};

const SRC = 'projects';
const L_CLUSTERS = 'projects-clusters';
const L_CLUSTER_COUNT = 'projects-cluster-count';
const L_POINT_HALO = 'projects-point-halo';
const L_POINTS = 'projects-points';
const ACCENT = '#4F7CFF';

function buildMapStyle(token: string, basemap: Basemap): maplibregl.StyleSpecification {
  const glyphs = 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf';
  if (!token || basemap === 'osm') {
    return {
      version: 8, glyphs,
      sources: { basemap: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' } },
      layers: [{ id: 'basemap-layer', type: 'raster', source: 'basemap', minzoom: 0, maxzoom: 19 }],
    };
  }
  const { mapboxStyle } = MAPBOX_STYLES[basemap];
  return {
    version: 8, glyphs,
    sources: { basemap: { type: 'raster', tiles: [`https://api.mapbox.com/styles/v1/mapbox/${mapboxStyle}/tiles/512/{z}/{x}/{y}@2x?access_token=${token}`], tileSize: 512, attribution: '&copy; <a href="https://www.mapbox.com/">Mapbox</a>' } },
    layers: [{ id: 'basemap-layer', type: 'raster', source: 'basemap', minzoom: 0, maxzoom: 22 }],
  };
}

async function geocodeAddress(address: string, token: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const resp = await fetch(`https://api.mapbox.com/search/geocode/v6/forward?q=${encodeURIComponent(address)}&limit=1&country=us&access_token=${token}`);
    if (!resp.ok) return null;
    const data = await resp.json();
    const coords = data.features?.[0]?.geometry?.coordinates;
    if (!coords || coords.length < 2) return null;
    return { lat: coords[1], lng: coords[0] };
  } catch { return null; }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

export function ProjectsMapView({
  projects, mode = 'portal', layout = 'embedded', onLocate, height, searchValue, onSearchChange, overlayTopLeft, panelExtras,
}: Props) {
  const isAdmin = mode === 'admin';
  const fullscreen = layout === 'fullscreen';
  const projectHref = useCallback((id: string) => (isAdmin ? `/admin/projects/${id}/view` : `/portal/projects/${id}`), [isAdmin]);
  const nameOf = useCallback((p: Project) => formatProjectName(p, { portal: !isAdmin }), [isAdmin]);

  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const hoverIdRef = useRef<string | null>(null);
  const lastFitKeyRef = useRef<string>('');
  const projectsRef = useRef<Project[]>(projects);
  projectsRef.current = projects;
  const navigate = useNavigate();

  const [geocoded, setGeocoded] = useState<Map<string, { lat: number; lng: number }>>(new Map());
  const [geocoding, setGeocoding] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [panelOpen, setPanelOpen] = useState(true);
  const [locating, setLocating] = useState(false);

  const search = searchValue ?? internalSearch;
  const setSearch = onSearchChange ?? setInternalSearch;
  const controlledSearch = onSearchChange !== undefined;

  const mapboxToken = (import.meta.env.VITE_MAPBOX_TOKEN as string | undefined) || '';
  const storageKey = isAdmin ? 'admin_map_basemap' : 'portal_map_basemap';
  const [basemap, setBasemap] = useState<Basemap>(() => {
    const saved = localStorage.getItem(storageKey) as Basemap | null;
    if (!mapboxToken) return 'osm';
    return saved && saved !== 'osm' ? saved : 'dark';
  });

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const getCoords = useCallback((p: Project): { lat: number; lng: number } | null => {
    if (p.latitude != null && p.longitude != null) return { lat: p.latitude, lng: p.longitude };
    return geocoded.get(p.id) || null;
  }, [geocoded]);
  const getCoordsRef = useRef(getCoords);
  getCoordsRef.current = getCoords;

  // Client-side geocode fallback for projects without stored coordinates
  useEffect(() => {
    if (!mapboxToken) { setGeocoding(false); return; }
    const toGeocode = projects.filter((p) => p.latitude == null && p.longitude == null && (p.address || p.city));
    if (toGeocode.length === 0) { setGeocoding(false); return; }
    let cancelled = false;
    (async () => {
      const results = new Map<string, { lat: number; lng: number }>();
      for (const p of toGeocode) {
        if (cancelled) break;
        const query = [p.address, p.city, p.state].filter(Boolean).join(', ');
        if (!query) continue;
        const coords = await geocodeAddress(query, mapboxToken);
        if (coords) results.set(p.id, coords);
      }
      if (!cancelled) { setGeocoded(results); setGeocoding(false); }
    })();
    return () => { cancelled = true; };
  }, [projects, mapboxToken]);

  // ───── Popup card ─────
  const cardHtml = useCallback((project: Project) => {
    const name = escapeHtml(nameOf(project));
    const clientName = isAdmin ? escapeHtml(((project as any).clientName as string | undefined) || '') : '';
    const location = escapeHtml([project.address, project.city, project.state].filter(Boolean).join(', '));
    return `
      <div class="projects-map-card">
        ${project.thumbnailPath
          ? `<img class="projects-map-card__media" src="${escapeHtml(project.thumbnailPath)}" alt="" />`
          : '<div class="projects-map-card__media projects-map-card__media--empty"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9.5 12 4l9 5.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><path d="M9 21v-7h6v7"/></svg></div>'}
        <div class="projects-map-card__body">
          <p class="projects-map-card__title">${name}</p>
          ${clientName ? `<p class="projects-map-card__client">${clientName}</p>` : ''}
          ${location ? `<p class="projects-map-card__meta">${location}</p>` : ''}
          ${project.category ? `<span class="projects-map-card__chip">${escapeHtml(project.category)}</span>` : ''}
          <div class="projects-map-card__cta"><span>Open project</span><span>&#8250;</span></div>
        </div>
      </div>`;
  }, [nameOf, isAdmin]);

  const setSelected = useCallback((id: string | null) => {
    const m = mapRef.current;
    if (!m || !m.getSource(SRC)) return;
    if (selectedIdRef.current) m.setFeatureState({ source: SRC, id: selectedIdRef.current }, { selected: false });
    selectedIdRef.current = id;
    if (id) m.setFeatureState({ source: SRC, id }, { selected: true });
    setActiveId(id);
  }, []);

  const openProject = useCallback((project: Project, fly = true) => {
    const m = mapRef.current;
    const coords = getCoordsRef.current(project);
    if (!m || !coords) return;
    popupRef.current?.remove();
    setSelected(project.id);
    const show = () => {
      const popup = new maplibregl.Popup({ offset: 14, closeButton: false, maxWidth: '280px', className: 'projects-map-popup' })
        .setLngLat([coords.lng, coords.lat])
        .setHTML(cardHtml(project))
        .addTo(m);
      popup.getElement()?.addEventListener('click', () => navigate(projectHref(project.id)));
      popup.on('close', () => { if (selectedIdRef.current === project.id) setSelected(null); });
      popupRef.current = popup;
    };
    if (fly) {
      m.flyTo({ center: [coords.lng, coords.lat], zoom: Math.max(m.getZoom(), 13), duration: 700 });
      m.once('moveend', show);
    } else {
      show();
    }
  }, [cardHtml, navigate, projectHref, setSelected]);

  // ───── Map init (re-created when the basemap changes) ─────
  useEffect(() => {
    if (!mapContainer.current) return;
    const m = new maplibregl.Map({
      container: mapContainer.current,
      style: buildMapStyle(mapboxToken, basemap),
      center: [-96, 38.5], zoom: 3.6, attributionControl: false,
    });
    m.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    m.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');

    m.on('load', () => {
      m.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, cluster: true, clusterRadius: 44, clusterMaxZoom: 14, promoteId: 'id' });
      m.addLayer({
        id: L_CLUSTERS, type: 'circle', source: SRC, filter: ['has', 'point_count'],
        paint: {
          'circle-color': ACCENT,
          'circle-radius': ['step', ['get', 'point_count'], 15, 10, 19, 50, 24],
          'circle-stroke-width': 2.5, 'circle-stroke-color': '#ffffff', 'circle-opacity': 0.96,
        },
      });
      m.addLayer({
        id: L_CLUSTER_COUNT, type: 'symbol', source: SRC, filter: ['has', 'point_count'],
        layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Open Sans Semibold'], 'text-size': 12, 'text-allow-overlap': true },
        paint: { 'text-color': '#ffffff' },
      });
      m.addLayer({
        id: L_POINT_HALO, type: 'circle', source: SRC, filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': ACCENT,
          'circle-radius': ['case', ['boolean', ['feature-state', 'selected'], false], 16, 0],
          'circle-opacity': 0.22,
        },
      });
      m.addLayer({
        id: L_POINTS, type: 'circle', source: SRC, filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': ['case', ['boolean', ['feature-state', 'hover'], false], '#6B95FF', ACCENT],
          'circle-radius': ['case', ['boolean', ['feature-state', 'selected'], false], 9, ['boolean', ['feature-state', 'hover'], false], 8.5, 7],
          'circle-stroke-width': 2.5, 'circle-stroke-color': '#ffffff',
        },
      });

      m.on('click', L_CLUSTERS, async (e: MapLayerMouseEvent) => {
        const f = e.features?.[0];
        if (!f) return;
        const clusterId = f.properties?.cluster_id as number;
        const src = m.getSource(SRC) as GeoJSONSource;
        try {
          const zoom = await src.getClusterExpansionZoom(clusterId);
          m.easeTo({ center: (f.geometry as GeoJSON.Point).coordinates as [number, number], zoom: zoom + 0.3, duration: 500 });
        } catch { /* ignore */ }
      });
      m.on('click', L_POINTS, (e: MapLayerMouseEvent) => {
        const id = e.features?.[0]?.properties?.id as string | undefined;
        const project = id ? projectsRef.current.find((p) => p.id === id) : undefined;
        if (project) openProject(project, false);
      });
      const setHover = (id: string | null) => {
        if (hoverIdRef.current) m.setFeatureState({ source: SRC, id: hoverIdRef.current }, { hover: false });
        hoverIdRef.current = id;
        if (id) m.setFeatureState({ source: SRC, id }, { hover: true });
      };
      m.on('mousemove', L_POINTS, (e: MapLayerMouseEvent) => { m.getCanvas().style.cursor = 'pointer'; setHover((e.features?.[0]?.properties?.id as string) || null); });
      m.on('mouseleave', L_POINTS, () => { m.getCanvas().style.cursor = ''; setHover(null); });
      m.on('mouseenter', L_CLUSTERS, () => { m.getCanvas().style.cursor = 'pointer'; });
      m.on('mouseleave', L_CLUSTERS, () => { m.getCanvas().style.cursor = ''; });

      setMapReady(true);
    });

    mapRef.current = m;
    return () => { setMapReady(false); popupRef.current = null; selectedIdRef.current = null; hoverIdRef.current = null; lastFitKeyRef.current = ''; m.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapboxToken, basemap]);

  const switchBasemap = (b: Basemap) => { setBasemap(b); localStorage.setItem(storageKey, b); };

  // ───── Push data + fit bounds ─────
  const mappable = useMemo(() => projects.filter((p) => getCoords(p) !== null), [projects, getCoords]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m || !mapReady || geocoding) return;
    const src = m.getSource(SRC) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: mappable.map((p) => {
        const c = getCoords(p)!;
        return { type: 'Feature', id: p.id, properties: { id: p.id }, geometry: { type: 'Point', coordinates: [c.lng, c.lat] } };
      }),
    });
    const fitKey = mappable.map((p) => p.id).sort().join('|');
    if (fitKey !== lastFitKeyRef.current) {
      lastFitKeyRef.current = fitKey;
      if (mappable.length === 1) {
        const c = getCoords(mappable[0])!;
        m.flyTo({ center: [c.lng, c.lat], zoom: 12 });
      } else if (mappable.length > 1) {
        const bounds = new maplibregl.LngLatBounds();
        for (const p of mappable) { const c = getCoords(p)!; bounds.extend([c.lng, c.lat]); }
        m.fitBounds(bounds, { padding: fullscreen && panelOpen && !isMobile ? { top: 80, bottom: 60, left: 60, right: 400 } : 60, maxZoom: 13 });
      }
    }
  }, [mappable, mapReady, geocoding, getCoords, fullscreen, panelOpen, isMobile]);

  // ───── Panel list ─────
  const listed = useMemo(() => {
    if (controlledSearch || !search.trim()) return projects;
    const q = search.toLowerCase();
    return projects.filter((p) => {
      const name = nameOf(p).toLowerCase();
      const loc = [p.address, p.city, p.state].filter(Boolean).join(' ').toLowerCase();
      const cat = (p.category || '').toLowerCase();
      const client = (((p as any).clientName as string | undefined) || '').toLowerCase();
      return name.includes(q) || loc.includes(q) || cat.includes(q) || client.includes(q);
    });
  }, [projects, search, nameOf, controlledSearch]);

  const handleLocate = async () => {
    if (!onLocate) return;
    setLocating(true);
    try { await onLocate(); } finally { setLocating(false); }
  };

  const unlocatedWithAddress = projects.filter((p) => getCoords(p) === null && (p.address || p.city)).length;

  const panel = (
    <aside className={`projects-map__panel${fullscreen ? ' projects-map__panel--floating' : ''}`} style={!fullscreen ? { width: isMobile ? '100%' : 320, ...(isMobile ? { height: 240, flexShrink: 0 } : {}) } : undefined}>
      <div className="projects-map__panel-top">
        <div className="projects-map__search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search projects" />
          {search && <button onClick={() => setSearch('')} aria-label="Clear search">&times;</button>}
        </div>
        <div className="projects-map__count">Showing <strong>{listed.length}</strong> project{listed.length === 1 ? '' : 's'}{mappable.length !== projects.length ? ` · ${mappable.length} on map` : ''}</div>
        {panelExtras && <div className="projects-map__extras">{panelExtras}</div>}
      </div>

      <div className="projects-map__list">
        {listed.length === 0 ? (
          <div className="projects-map__empty">{search ? 'No projects match your search' : 'No projects'}</div>
        ) : listed.map((p) => {
          const coords = getCoords(p);
          const clientName = isAdmin ? ((p as any).clientName as string | undefined) : undefined;
          const line2 = p.address || '';
          const line3 = [p.city, p.state].filter(Boolean).join(', ');
          return (
            <button
              key={p.id}
              className={`projects-map__item${activeId === p.id ? ' projects-map__item--active' : ''}`}
              onClick={() => coords ? openProject(p) : navigate(projectHref(p.id))}
              onDoubleClick={() => navigate(projectHref(p.id))}
              title={coords ? 'Click to locate, double-click to open' : 'Open project'}
            >
              <span className="projects-map__item-title">{nameOf(p)}</span>
              {clientName && <span className="projects-map__item-client">{clientName}</span>}
              {line2 && <span className="projects-map__item-meta">{line2}</span>}
              {line3 && <span className="projects-map__item-meta">{line3}</span>}
              {!coords && <span className="projects-map__item-nopin">No location</span>}
            </button>
          );
        })}
      </div>
    </aside>
  );

  const overlays = (
    <>
      {overlayTopLeft && <div className="projects-map__overlay-tl">{overlayTopLeft}</div>}

      {fullscreen && !isMobile && (
        <button className="projects-map__panel-toggle" style={{ right: panelOpen ? 356 : 16 }} onClick={() => setPanelOpen((v) => !v)} title={panelOpen ? 'Hide list' : 'Show list'}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: panelOpen ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 200ms' }}><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      )}

      <div className="projects-map__overlay-bl">
        {mapboxToken && (
          <div className="projects-map__segment">
            {(Object.keys(MAPBOX_STYLES) as Array<Exclude<Basemap, 'osm'>>).map((b) => (
              <button key={b} onClick={() => switchBasemap(b)} className={basemap === b ? 'is-active' : ''}>{MAPBOX_STYLES[b].label}</button>
            ))}
          </div>
        )}
        {geocoding && <div className="projects-map__pill">Locating projects…</div>}
        {isAdmin && onLocate && !geocoding && unlocatedWithAddress > 0 && (
          <div className="projects-map__pill projects-map__pill--warn">
            <span>{unlocatedWithAddress} project{unlocatedWithAddress === 1 ? '' : 's'} without a pin</span>
            <button onClick={handleLocate} disabled={locating}>{locating ? 'Locating…' : 'Locate'}</button>
          </div>
        )}
      </div>

      {!geocoding && mappable.length === 0 && (
        <div className="projects-map__nodata">
          <div>No project locations yet</div>
          <small>Projects need an address or coordinates to appear on the map.</small>
        </div>
      )}
    </>
  );

  if (fullscreen) {
    return (
      <div className="projects-map projects-map--fullscreen">
        <div ref={mapContainer} className="projects-map__canvas" />
        {overlays}
        {(panelOpen || isMobile) && panel}
      </div>
    );
  }

  return (
    <div className="projects-map projects-map--embedded" style={{ flexDirection: isMobile ? 'column-reverse' : 'row', height: height ?? 'calc(100vh - 200px)' }}>
      <div className="projects-map__stage">
        <div ref={mapContainer} className="projects-map__canvas" />
        {overlays}
      </div>
      {panel}
    </div>
  );
}
